<template>
  <div class="live-captions-view p-3">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div class="text-start">
        <h2 class="fw-bold mb-0">{{ $t("message.liveCaptions") }}</h2>
        <p class="text-muted mb-0">{{ $t("message.liveCaptionsSubtitle") }}</p>
      </div>
      <span class="badge rounded-pill live-badge px-3 py-2">
        <span class="live-dot"></span> {{ $t("message.liveNow") }}
      </span>
    </div>

    <div class="card p-3 mb-4 bg-light border-0">
      <div class="row align-items-center">
        <div class="col-md-6 text-start">
          <label class="form-label small text-muted mb-1">{{ $t("message.translationLanguage") }}</label>
          <select class="form-select" v-model="selectedLang">
            <option v-for="lang in languageOptions" :key="lang" :value="lang">
              {{ toLangLabel(lang) }}
            </option>
          </select>
        </div>
      </div>
    </div>

    <div v-if="isLoading" class="py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="mt-2">{{ $t("message.loadingLiveCaptions") }}</p>
    </div>

    <div v-else-if="!latestCaption" class="py-5 text-muted">
      <span class="material-icons display-4 mb-3">subtitles_off</span>
      <p>{{ $t("message.awaitingLiveCaptions") }}</p>
    </div>

    <div v-else class="caption-container text-start">
      <div class="caption-card card shadow-sm p-4">
        <div class="source-lang-meta mb-2">
          <span class="text-muted small">
            {{ sourceLang ? `${$t("message.originalLanguage")}: ${toLangLabel(sourceLang)}` : $t("message.originalLanguage") }}
          </span>
        </div>
        <h1 class="original-text mb-4">
          {{ latestCaption.original || "" }}
        </h1>
        
        <hr class="my-4 opacity-10">
        
        <div class="target-lang-meta mb-2">
          <span class="text-muted small">
            {{ $t("message.selectedTranslation") }}: {{ toLangLabel(selectedLang) }}
          </span>
        </div>
        <transition-group name="translation-slide" tag="div" class="translation-list">
          <div
            v-for="(caption, idx) in visibleCaptions"
            :key="caption.sequence || caption.createdAt || caption.original"
            class="translation-entry mb-2"
            :class="{ 'translation-entry-latest': idx === 0 }"
          >
            <div class="translation-row">
              <div class="translation-text" :class="{ 'translation-text-latest': idx === 0 }">
                {{ getDisplayTranslation(caption) }}
              </div>
              <div class="translation-time">
                {{ formatCaptionTime(caption) }}
              </div>
            </div>
            <div v-if="isFallback(caption)" class="alert alert-warning mt-2 py-2 small mb-0 d-inline-block">
              <span class="material-icons small align-middle me-1">info</span>
              {{ $t("message.translationFallback") }}
            </div>
          </div>
        </transition-group>
      </div>
    </div>
  </div>
</template>

<script>
import API from "../API.js";

const POLL_MS = 1800;
const CAPTION_META_KEYS = new Set(["sequence", "createdAt", "sourceLang", "original", "translations", "lang", "isDraft", "status"]);

export default {
  name: "LiveCaptions",
  data() {
    return {
      captions: [],
      availableLanguages: [],
      selectedLang: "en",
      isLoading: true,
      pollInterval: null,
      latestSequence: 0,
      streamStartedAt: "",
      translatedHistory: [],
    };
  },
  computed: {
    latestCaption() {
      return this.captions.length ? this.captions[this.captions.length - 1] : null;
    },
    visibleCaptions() {
      return this.translatedHistory.slice(-5).reverse();
    },
    sourceLang() {
      return this.inferSourceLang(this.latestCaption);
    },
    languageOptions() {
      const unique = new Set(["original", ...this.availableLanguages]);
      this.captions.forEach((caption) => {
        const langs = this.extractLangsFromCaption(caption);
        langs.forEach((lang) => unique.add(lang));
      });
      return Array.from(unique).filter((lang) => !!lang && lang !== "sourceLang" && lang !== "translations" && lang !== "draft");
    },
  },
  created() {
    // Set initial language from i18n
    const locale = this.$i18n ? this.$i18n.locale : "en";
    const appLang = (typeof locale === "string" ? locale : (locale.value || "en")).toLowerCase();
    this.selectedLang = appLang;
  },
  mounted() {
    this.refresh(true);
    this.pollInterval = setInterval(() => {
      this.refresh(false);
    }, POLL_MS);
  },
  beforeUnmount() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
    }
  },
  watch: {
    languageOptions(newOptions) {
      if (newOptions.includes(this.selectedLang)) return;
      const locale = this.$i18n ? this.$i18n.locale : "en";
      const appLang = (typeof locale === "string" ? locale : (locale.value || "en")).toLowerCase();
      if (newOptions.includes(appLang)) {
        this.selectedLang = appLang;
        return;
      }
      if (newOptions.includes("en")) {
        this.selectedLang = "en";
        return;
      }
      if (newOptions.length) this.selectedLang = newOptions[0];
    },
  },
  methods: {
    toLangLabel(lang = "") {
      const normalized = String(lang || "").toLowerCase();
      if (!normalized || normalized === "original") return this.$t("message.originalLanguage");
      if (normalized === "en") return this.$t("message.languageEnglish");
      if (normalized === "es") return this.$t("message.languageSpanish");
      if (normalized === "fr") return this.$t("message.languageFrench");
      if (normalized === "da") return this.$t("message.languageDanish");
      if (normalized === "ar") return this.$t("message.languageArabic");
      return normalized.toUpperCase();
    },
    async refresh(initial = false) {
      try {
        const sinceSequence = initial ? undefined : this.latestSequence;
        const response = await API.getLiveCaptions(sinceSequence, 50);
        const incoming = response.captions;
        const languages = response.availableLanguages;
        const startedAt = (typeof response.startedAt === "string") ? response.startedAt : "";
        const hasStreamReset =
          (this.streamStartedAt && startedAt && this.streamStartedAt !== startedAt)
          || (Number.isFinite(response.latestSequence) && response.latestSequence < this.latestSequence);

        if (hasStreamReset) {
          this.latestSequence = 0;
          this.captions = [];
          this.translatedHistory = [];
        }
        if (startedAt) this.streamStartedAt = startedAt;

        if (initial || hasStreamReset) {
          this.captions = incoming;
          this.translatedHistory = this.mergeHistoryEntries([], incoming);
        } else if (incoming.length > 0) {
          const bySequence = new Map(this.captions.map((item) => [item.sequence, item]));
          incoming.forEach((item) => {
            if (!item || !Number.isFinite(item.sequence)) return;
            bySequence.set(item.sequence, item);
          });
          this.captions = Array.from(bySequence.values())
            .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
            .slice(-150);
          this.translatedHistory = this.mergeHistoryEntries(this.translatedHistory, incoming);
        }

        if (Number.isFinite(response.latestSequence)) {
          this.latestSequence = response.latestSequence;
        } else if (incoming.length) {
          const maxSeq = incoming.reduce((acc, item) => Math.max(acc, Number(item && item.sequence) || 0), this.latestSequence);
          this.latestSequence = maxSeq;
        }

        this.availableLanguages = languages;
        this.isLoading = false;
      } catch (error) {
        console.error("Error fetching live captions:", error);
        this.isLoading = false;
      }
    },
    isDraftCaptionItem(item) {
      if (!item || typeof item !== "object") return false;
      if (item.isDraft === true) return true;
      const status = String(item.status || "").toLowerCase();
      if (status === "draft") return true;
      const lang = String(item.lang || "").toLowerCase();
      return lang === "draft";
    },
    findPreviousFinalCaption(item) {
      if (!item || !Number.isFinite(item.sequence)) return null;
      for (let idx = this.captions.length - 1; idx >= 0; idx -= 1) {
        const candidate = this.captions[idx];
        if (!candidate || !Number.isFinite(candidate.sequence)) continue;
        if (candidate.sequence > item.sequence) continue;
        if (!this.isDraftCaptionItem(candidate)) return candidate;
      }
      return null;
    },
    getDisplayTranslation(item) {
      const translation = this.getTextForLang(item, this.selectedLang);
      const previousFinal = this.findPreviousFinalCaption(item);
      const previousFinalTranslation = this.getTextForLang(previousFinal, this.selectedLang);
      const isDraft = this.isDraftCaptionItem(item);
      const isTranslating = isDraft && this.selectedLang && this.selectedLang !== "original";
      return translation || previousFinalTranslation || (isTranslating ? this.$t("message.translating") : ((item && item.original) || ""));
    },
    formatCaptionTime(item) {
      const raw = item && item.createdAt;
      const date = raw ? new Date(raw) : new Date();
      if (Number.isNaN(date.getTime())) return "";
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });
    },
    isFallback(item) {
      if (!item) return false;
      const translation = this.getTextForLang(item, this.selectedLang);
      const previousFinal = this.findPreviousFinalCaption(item);
      const previousFinalTranslation = this.getTextForLang(previousFinal, this.selectedLang);
      const isDraft = this.isDraftCaptionItem(item);
      const isTranslating = isDraft && this.selectedLang && this.selectedLang !== "original";
      const sourceLang = this.inferSourceLang(item);
      return !translation && !previousFinalTranslation && !isTranslating && this.selectedLang && this.selectedLang !== "original" && this.selectedLang !== sourceLang;
    },
    buildHistoryEntry(item) {
      if (!item || typeof item !== "object" || this.isDraftCaptionItem(item)) return null;
      if (!Number.isFinite(item.sequence)) return null;
      const translationMap = {};

      const nestedMap = item.translations;
      if (nestedMap && typeof nestedMap === "object" && !Array.isArray(nestedMap)) {
        Object.entries(nestedMap).forEach(([lang, text]) => {
          const normalizedLang = String(lang || "").toLowerCase().trim();
          const normalizedText = typeof text === "string" ? text.trim() : "";
          if (!normalizedLang || normalizedLang === "draft" || !normalizedText) return;
          translationMap[normalizedLang] = normalizedText;
        });
      }

      Object.entries(item).forEach(([key, value]) => {
        if (CAPTION_META_KEYS.has(key)) return;
        const normalizedLang = String(key || "").toLowerCase().trim();
        const normalizedText = typeof value === "string" ? value.trim() : "";
        if (!normalizedLang || normalizedLang === "draft" || !normalizedText) return;
        translationMap[normalizedLang] = normalizedText;
      });

      if (!Object.keys(translationMap).length) return null;
      return {
        sequence: item.sequence,
        createdAt: item.createdAt,
        sourceLang: this.inferSourceLang(item),
        original: typeof item.original === "string" ? item.original : "",
        translations: translationMap,
      };
    },
    mergeHistoryEntries(previous, incoming) {
      if (!Array.isArray(incoming) || !incoming.length) return previous || [];
      const bySequence = new Map((previous || []).map((entry) => [entry.sequence, entry]));
      incoming.forEach((item) => {
        const entry = this.buildHistoryEntry(item);
        if (!entry) return;
        bySequence.set(entry.sequence, entry);
      });
      return Array.from(bySequence.values()).sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
    },
    inferSourceLang(item) {
      if (!item || typeof item !== "object") return "";
      if (item.sourceLang && String(item.sourceLang).toLowerCase() !== "draft") return String(item.sourceLang).toLowerCase();
      if (item.lang && String(item.lang).toLowerCase() !== "draft") return String(item.lang).toLowerCase();
      const original = typeof item.original === "string" ? item.original.trim() : "";
      if (!original) return "";
      const direct = Object.entries(item).find(
        ([key, value]) => !CAPTION_META_KEYS.has(key) && typeof value === "string" && value.trim() === original
      );
      if (direct && direct[0]) return String(direct[0]).toLowerCase();
      const nested = Object.entries(item.translations || {}).find(
        ([, value]) => typeof value === "string" && value.trim() === original
      );
      return (nested && nested[0]) ? String(nested[0]).toLowerCase() : "";
    },
    getTextForLang(item, lang) {
      if (!item || typeof item !== "object") return "";
      const normalizedLang = String(lang || "").toLowerCase();
      if (!normalizedLang || normalizedLang === "original") return item.original || "";
      if (normalizedLang === "draft") return "";
      const direct = typeof item[normalizedLang] === "string" ? item[normalizedLang].trim() : "";
      if (direct) return direct;
      const nested = (item.translations && typeof item.translations[normalizedLang] === "string") ? item.translations[normalizedLang].trim() : "";
      if (nested) return nested;
      return "";
    },
    extractLangsFromCaption(caption) {
      const langs = new Set();
      if (!caption || typeof caption !== "object") return langs;
      if (caption.sourceLang && String(caption.sourceLang).toLowerCase() !== "draft") langs.add(String(caption.sourceLang).toLowerCase());
      if (caption.lang && String(caption.lang).toLowerCase() !== "draft") langs.add(String(caption.lang).toLowerCase());

      const map = caption.translations;
      if (map && typeof map === "object" && !Array.isArray(map)) {
        Object.keys(map).forEach((lang) => {
          if (lang) langs.add(String(lang).toLowerCase());
        });
      }

      Object.entries(caption).forEach(([key, value]) => {
        if (CAPTION_META_KEYS.has(key)) return;
        if (typeof value !== "string" || !value.trim()) return;
        const lang = String(key || "").toLowerCase().trim();
        if (!lang || lang === "draft") return;
        langs.add(lang);
      });

      return langs;
    },
  },
};
</script>

<style scoped>
.live-badge {
  background-color: #ecfdf5;
  color: #047857;
  font-weight: 600;
  display: flex;
  align-items: center;
}

.live-dot {
  width: 8px;
  height: 8px;
  background-color: #059669;
  border-radius: 50%;
  margin-right: 8px;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(5, 150, 105, 0.7); }
  70% { transform: scale(1); box-shadow: 0 0 0 6px rgba(5, 150, 105, 0); }
  100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(5, 150, 105, 0); }
}

.caption-card {
  border-radius: 1.25rem;
  border: 1px solid #e5e7eb;
}

.original-text {
  font-size: 1.2rem;
  line-height: 1.4;
  font-weight: 400;
  color: #4b5563;
}

.translation-text {
  color: #374151;
  font-weight: 500;
  font-size: 1.45rem;
  line-height: 1.35;
}

.translation-row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 0.5rem;
}

.translation-time {
  flex-shrink: 0;
  font-size: 0.72rem;
  color: #9ca3af;
}

.translation-entry {
  background-color: #f9fafb;
  border-radius: 0.75rem;
  padding: 0.75rem;
}

.translation-text-latest {
  font-size: 1.67rem;
  line-height: 1.35;
  font-weight: 700;
}

.translation-slide-enter-active,
.translation-slide-leave-active,
.translation-slide-move {
  transition: all 220ms ease;
}

.translation-slide-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.translation-slide-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

@media (max-width: 768px) {
  .original-text {
    font-size: 1rem;
  }
  .translation-text {
    font-size: 1.2rem;
  }
}
</style>
